// AIBotPage — AI Bot Trader (the marquee feature)
// Layout:
//   1. Top nav: < Back | "AI Bot Trader" | History icon
//   2. Hero status card: bot emoji, name, status pill, today's P&L
//   3. Stats grid: Today / This week / Win rate / Trades
//   4. Equity chart (the bot's P&L curve)
//   5. Strategy selector (4 strategies with tier-locked indicators)
//   6. Open positions
//   7. Recent trades
//   8. Settings: risk %, max drawdown, allowed pairs
//   9. Big "Start" / "Pause" / "Stop" button

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TIERS, DEMO_BOT, BOT_STRATEGIES, DEMO_CHALLENGE } from '../../data/platforms';
import {
  TfCard,
  TfCardList,
  TfBadge,
  TfSectionHeaderView,
  TfSparklineMarker,
  TfTimeRange,
  TfStatRow,
} from '../../components/TfPrimitives';

const STATUS_BG: Record<string, string> = {
  running: 'bg-tf-profit',
  paused: 'bg-tf-warning',
  stopped: 'bg-tf-text-3',
};

const RANGE_OPTIONS = ['1D', '7D', '1M', '3M', '6M', '1Y'] as const;

export function AIBotPage() {
  const nav = useNavigate();
  const userTier = DEMO_CHALLENGE.tier;
  const tier = TIERS[userTier];
  const [bot, setBot] = useState(DEMO_BOT);
  const [risk, setRisk] = useState(0.5);  // 0..2 %
  const [maxDD, setMaxDD] = useState(1500);
  const [pairs, setPairs] = useState<string[]>(['ES', 'NQ']);
  const [range, setRange] = useState<typeof RANGE_OPTIONS[number]>('1D');

  const strategy = BOT_STRATEGIES.find(s => s.key === bot.strategy)!;

  // Determine which strategies are locked
  const tierOrder = { Beginner: 0, Mid: 1, Professional: 2 } as const;
  function isStrategyLocked(sKey: string) {
    const s = BOT_STRATEGIES.find(x => x.key === sKey)!;
    return tierOrder[tier.key] < tierOrder[s.tierRequired];
  }

  function togglePair(p: string) {
    setPairs(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  }

  function startStop() {
    if (bot.status === 'running') {
      setBot({ ...bot, status: 'stopped' });
    } else {
      setBot({ ...bot, status: 'running' });
    }
  }

  // Generate equity curve
  const equityPoints = useMemo(() => {
    const out: number[] = [];
    let v = 10000;
    for (let i = 0; i < 60; i++) {
      v += (Math.sin(i * 0.4) * 80) + (Math.random() - 0.45) * 200;
      out.push(v);
    }
    return out;
  }, []);

  return (
    <div className="min-h-screen bg-tf-bg pb-[calc(96px+env(safe-area-inset-bottom))]">
      {/* Top nav */}
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">AI Bot Trader</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero status card */}
      <div className="px-4 pt-5">
        <div className="overflow-hidden rounded-[20px] bg-gradient-to-br from-tf-text-1 via-tf-text-1 to-tf-text-1 p-5 text-white" style={{ background: 'linear-gradient(135deg, #1C1C1E 0%, #2A2A2A 100%)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-[14px] bg-white/10 text-[28px]">
                {strategy.icon}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[17px] font-bold">{strategy.name} Bot</span>
                  <span className={['flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                    bot.status === 'running' ? 'bg-tf-profit/20 text-tf-profit' : 'bg-white/15 text-white/70'].join(' ')}>
                    <span className={['h-1.5 w-1.5 rounded-full', STATUS_BG[bot.status]].join(' ')} />
                    {bot.status.toUpperCase()}
                  </span>
                </div>
                <div className="mt-0.5 text-[12px] text-white/70">
                  {bot.platform} · ${(bot.accountSize / 1000).toFixed(0)}K account
                </div>
              </div>
            </div>
          </div>

          {/* Hero P&L */}
          <div className="mt-5 flex items-end justify-between">
            <div>
              <div className="text-[12px] text-white/60">Today P&L</div>
              <div className={['text-[34px] font-bold tabular-nums leading-none', bot.todayPnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                {bot.todayPnl >= 0 ? '+' : ''}${bot.todayPnl.toFixed(2)}
              </div>
              <div className={['mt-1 text-[13px] tabular-nums', bot.todayPct >= 0 ? 'text-tf-profit/80' : 'text-tf-loss/80'].join(' ')}>
                {bot.todayPct >= 0 ? '+' : ''}{bot.todayPct.toFixed(2)}% on ${(bot.accountSize / 1000).toFixed(0)}K
              </div>
            </div>
            <div className="text-right">
              <div className="text-[12px] text-white/60">This week</div>
              <div className={['text-[20px] font-bold tabular-nums', bot.weekPnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                {bot.weekPnl >= 0 ? '+' : ''}${bot.weekPnl.toFixed(0)}
              </div>
              <div className={['text-[12px] tabular-nums', bot.weekPct >= 0 ? 'text-tf-profit/80' : 'text-tf-loss/80'].join(' ')}>
                {bot.weekPct >= 0 ? '+' : ''}{bot.weekPct.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="mx-4 mt-3">
        <TfStatRow
          stats={[
            { label: 'Trades today', value: bot.tradesToday.toString() },
            { label: 'Win rate', value: `${(bot.winRate * 100).toFixed(0)}%`, color: '#34C759' },
            { label: 'Open positions', value: bot.openPositions.length.toString() },
          ]}
        />
      </div>

      {/* Equity curve */}
      <TfCard className="mx-4 mt-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[12px] text-tf-text-3">Bot equity</div>
            <div className="text-[15px] font-semibold text-tf-text-1">${(equityPoints[equityPoints.length - 1]).toFixed(0)}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-tf-text-3">vs $10K</div>
            <div className={['text-[14px] font-semibold tabular-nums',
              equityPoints[equityPoints.length - 1] >= 10000 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
              {equityPoints[equityPoints.length - 1] >= 10000 ? '+' : ''}
              {(((equityPoints[equityPoints.length - 1] - 10000) / 10000) * 100).toFixed(1)}%
            </div>
          </div>
        </div>
        <div className="-mx-1">
          <TfSparklineMarker
            points={equityPoints}
            color={bot.todayPnl >= 0 ? '#34C759' : '#FF3B30'}
            height={100}
            markerIndex={Math.floor(equityPoints.length * 0.6)}
            markerLabel={`$${equityPoints[Math.floor(equityPoints.length * 0.6)].toFixed(0)}`}
            markerPct={(((equityPoints[Math.floor(equityPoints.length * 0.6)] - 10000) / 10000) * 100)}
          />
        </div>
        <TfTimeRange value={range} onChange={setRange} options={[...RANGE_OPTIONS]} />
      </TfCard>

      {/* Strategy selector */}
      <TfSectionHeaderView title="Strategy" viewAllLabel="Compare" onViewAll={() => nav('/portal/bot/marketplace')} />
      <div className="px-4 space-y-2">
        {BOT_STRATEGIES.map(s => {
          const selected = s.key === bot.strategy;
          const locked = isStrategyLocked(s.key);
          return (
            <button
              key={s.key}
              onClick={() => !locked && setBot({ ...bot, strategy: s.key })}
              disabled={locked}
              data-testid={`strategy-${s.key}`}
              className={['flex w-full items-start gap-3 rounded-[14px] border p-3 text-left transition bg-tf-bg',
                selected ? 'border-tf-blue bg-tf-blue/[0.05]' : 'border-tf-border',
                locked ? 'opacity-50' : 'active:scale-[0.99]'].join(' ')}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[22px]" style={{ backgroundColor: s.color + '15' }}>
                {s.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold text-tf-text-1">{s.name}</span>
                  <TfBadge variant={s.riskLevel === 'Low' ? 'success' : s.riskLevel === 'Medium' ? 'warning' : 'danger'}>
                    {s.riskLevel} risk
                  </TfBadge>
                  {locked && <TfBadge variant="info">🔒 {s.tierRequired}</TfBadge>}
                </div>
                <div className="mt-0.5 truncate text-[12px] text-tf-text-3">{s.tagline}</div>
                <div className="mt-1 flex items-center gap-1">
                  {s.pairs.map(p => (
                    <span key={p} className="rounded bg-tf-surface-1 px-1.5 py-0.5 text-[10px] font-mono text-tf-text-3">{p}</span>
                  ))}
                </div>
              </div>
              {selected && (
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tf-blue">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Open positions */}
      {bot.openPositions.length > 0 && (
        <>
          <TfSectionHeaderView title={`Open positions (${bot.openPositions.length})`} />
          <div className="mx-4">
            <TfCardList>
              {bot.openPositions.map((p, i) => (
                <div key={i} className={['flex items-center justify-between px-4 py-3', i < bot.openPositions.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
                  <div className="flex items-center gap-3">
                    <span className={['flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold',
                      p.side === 'long' ? 'bg-tf-profit/15 text-tf-profit' : 'bg-tf-loss/15 text-tf-loss'].join(' ')}>
                      {p.side === 'long' ? 'L' : 'S'}
                    </span>
                    <div>
                      <div className="text-[14px] font-semibold text-tf-text-1">{p.pair}</div>
                      <div className="text-[11px] text-tf-text-3">Entry {p.entry.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[14px] font-semibold tabular-nums text-tf-text-1">{p.current.toFixed(2)}</div>
                    <div className={['text-[12px] tabular-nums', p.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                      {p.pnl >= 0 ? '+' : ''}${p.pnl.toFixed(2)} ({p.pnlPct.toFixed(2)}%)
                    </div>
                  </div>
                </div>
              ))}
            </TfCardList>
          </div>
        </>
      )}

      {/* Bot settings */}
      <TfSectionHeaderView title="Settings" viewAllLabel="More" onViewAll={() => nav('/portal/bot/config')} />
      <TfCard className="mx-4">
        {/* Risk per trade */}
        <div className="px-1 py-3 border-b border-tf-border">
          <div className="flex items-center justify-between">
            <span className="text-[14px] text-tf-text-1">Risk per trade</span>
            <span className="text-[14px] font-semibold tabular-nums text-tf-text-1">{risk.toFixed(1)}%</span>
          </div>
          <input
            type="range" min="0.1" max={tier.hasCustomRisk ? 2 : 0.5} step="0.1"
            value={risk} onChange={(e) => setRisk(parseFloat(e.target.value))}
            data-testid="risk-slider"
            className="mt-2 w-full accent-tf-blue"
          />
          {!tier.hasCustomRisk && (
            <div className="mt-1 text-[11px] text-tf-text-3">Custom risk available on Mid tier and above</div>
          )}
        </div>
        {/* Max drawdown */}
        <div className="px-1 py-3 border-b border-tf-border">
          <div className="flex items-center justify-between">
            <span className="text-[14px] text-tf-text-1">Max drawdown</span>
            <span className="text-[14px] font-semibold tabular-nums text-tf-text-1">${maxDD.toLocaleString()}</span>
          </div>
          <input
            type="range" min="500" max="3000" step="100"
            value={maxDD} onChange={(e) => setMaxDD(parseInt(e.target.value))}
            data-testid="maxdd-slider"
            className="mt-2 w-full accent-tf-blue"
          />
        </div>
        {/* Allowed pairs */}
        <div className="px-1 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[14px] text-tf-text-1">Allowed pairs</span>
            <span className="text-[11px] text-tf-text-3">{pairs.length} selected</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['ES', 'NQ', 'YM', 'RTY', 'CL', 'GC', 'BTC'].map(p => {
              const on = pairs.includes(p);
              return (
                <button
                  key={p}
                  onClick={() => togglePair(p)}
                  data-testid={`pair-${p}`}
                  className={['rounded-full px-3 py-1 text-[12px] font-semibold transition',
                    on ? 'bg-tf-blue text-white' : 'bg-tf-surface-1 text-tf-text-1'].join(' ')}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>
      </TfCard>

      {/* Action button (sticky bottom) */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-tf-border bg-tf-bg/95 backdrop-blur-xl" style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom))' }}>
        <div className="mx-auto max-w-[640px] px-4 py-3 md:px-6">
          <button
            onClick={startStop}
            data-testid="bot-toggle"
            className={['flex h-[52px] w-full items-center justify-center gap-2 rounded-full text-[16px] font-semibold text-white active:scale-[0.99]',
              bot.status === 'running' ? 'bg-tf-loss' : bot.status === 'paused' ? 'bg-tf-warning' : 'bg-tf-profit'].join(' ')}
          >
            {bot.status === 'running' ? (
              <>
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                Stop bot
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                Start bot
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
